/** @mui/material imports */
import Alert from '@mui/material/Alert/Alert';
import AlertTitle from '@mui/material/AlertTitle/AlertTitle';
import Button from '@mui/material/Button/Button';
import Dialog from '@mui/material/Dialog/Dialog';
import DialogActions from '@mui/material/DialogActions/DialogActions';
import DialogContent from '@mui/material/DialogContent/DialogContent';
import DialogTitle from '@mui/material/DialogTitle/DialogTitle';
import FormControl from '@mui/material/FormControl/FormControl';
import FormHelperText from '@mui/material/FormHelperText/FormHelperText';
import Stack from '@mui/material/Stack/Stack';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
/** @mui/icons-material imports */
/** React imports */
import { useEffect, useState, useRef, useContext } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
/** App imports */
import { ProjectObject } from '../../TypeScript/interfaces';
import {
  createProject,
  getProject,
} from '../../utils/authenticated-requests/projects';
import { errorHandler } from '../../utils/error/error-handler';
import { DebouncedTextField } from '../../utils/components/DebouncedTextField';
import { Unit } from '../../TypeScript/enums';
import { appContext } from '../../Contexts/AppContext';

const DEFAULT_RC_CHIP_INTERVAL = 1;
const MIN_RC_CHIP_INTERVAL = 1;
const MAX_RC_CHIP_INTERVAL = 100;

function debounce(func: Function, wait: number) {
  let timeout: any;
  return function executedFunction(...args: any) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export interface CreateProjectProps {
  isOpen: boolean;
  onClose: (projectId?: string) => void;
}

const CreateProjectPartial: React.FC<CreateProjectProps> = (
  props: CreateProjectProps,
): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [projectId, setProjectId] = useState<string>('');
  const [autoProjectId, setAutoProjectId] = useState<boolean>(true);
  const [errorProjectId, setErrorProjectId] = useState<string | null>(null);
  const [errorRCChipInterval, setErrorRCChipInterval] = useState<string | null>(
    null,
  );
  const [projectTitle, setProjectTitle] = useState<string>('');
  const [projectDescription, setProjectDescription] = useState<string>('');
  const [projectUnit, setProjectUnit] = useState<Unit>(Unit.Meter);
  const [projectRCChipInterval, setProjectRCChipInterval] = useState<number>(
    DEFAULT_RC_CHIP_INTERVAL,
  );
  const [formError, setFormError] = useState<string>();
  const { company, setAppLoading } = useContext(appContext);
  const client = useQueryClient();

  // Creating debounced function
  // UseRef so timouts remain after state change
  const debouncedValidate = useRef(
    debounce((projectId: any) => {
      // your effect code here
      validateProjectId(projectId);
    }, 500),
  ).current;

  useEffect(() => {
    if (projectId && projectId !== '') {
      debouncedValidate(projectId);
    } else {
      setAutoProjectId(true);
    }
  }, [projectId]);

  const handleSubmit = (): void => {
    if (projectId && projectTitle) {
      setAppLoading(true);
      createProject(company, projectId, {
        title: projectTitle,
        description: projectDescription,
        unit: projectUnit,
        type: process.env.REACT_APP_PROJECT_TYPE || 'litholens',
        rc_chip_interval: projectRCChipInterval,
      })
        .then((response: { message?: string; error?: string }) => {
          if (response.error) {
            setFormError(response.message || response.error);
          } else {
            // Redirect to the newly created project
            navigate(`projects/${projectId}`);
            client.invalidateQueries({ queryKey: ['projects'] });
            handleClose(projectId);
          }
        })
        .catch((err) => {
          setFormError(err.error?.response?.data?.error || err.message);
          errorHandler(err);
        })
        .finally(() => {
          setAppLoading(false);
        });
    }
  };

  const handleChange = (value: string, name: string): void => {
    if (formError) {
      setFormError(undefined);
    }
    switch (name) {
      // case 'addProjectId': will be controlled as we need to set it
      case 'addProjectTitle':
        setProjectTitle(value);
        if (autoProjectId && value != '') {
          let proposeId = autoGenerateId(value);
          setProjectId(proposeId);
        }
        break;
      case 'addProjectDescription':
        setProjectDescription(value);
        break;
    }
  };

  const handleUnitChange = ({ target: { value } }: SelectChangeEvent) => {
    setProjectUnit(value as Unit);
  };

  const handleClose = (projectId?: string): void => {
    if (!projectId) {
      reset();
    }
    props.onClose(projectId);
  };

  const validateProjectId = (projectId: string): void => {
    const projectIdRegex: RegExp = /^[a-z][0-9a-z\-]*[0-9a-z]$/;
    if (projectIdRegex.test(projectId)) {
      getProject(company, projectId)
        .then((response: ProjectObject) => {
          if (response && response.id) {
            setErrorProjectId(t('ERROR_PROJECTID_EXISTS'));
          } else {
            setErrorProjectId(null);
          }
        })
        .catch((err) => {
          setErrorProjectId(null);
        });
    } else {
      setErrorProjectId(t('ERROR_PROJECTID_REGEX'));
    }
  };

  // Propose a project ID to the user based on project Name
  // Project valid reg ex is ^[a-z][0-9a-z\-]*[0-9a-z]$
  const autoGenerateId = (string: string): string => {
    // Convert to lowercase and remove non-alphanumeric characters except spaces and hyphens
    const filteredStr = string
      .toLowerCase()
      .replace(/[^0-9a-z\s-]/g, '')
      .replace(/\s+/g, '-');

    // Ensure the first character is a letter
    const firstLetter = RegExp(/[a-z]/).exec(filteredStr);
    if (!firstLetter) {
      return '';
    }

    // Remove leading characters until the first letter
    const startIndex = filteredStr.indexOf(firstLetter[0]);
    // const startIndex = 2
    const id = filteredStr.slice(startIndex);

    // Ensure the last character is alphanumeric
    if (!RegExp(/[0-9a-z]$/).exec(id)) {
      return id.slice(0, -1);
    }

    return id;
  };

  // Handle RC Chip Interval change with validation
  const handleRCChipIntervalChange = (value: number): void => {
    setProjectRCChipInterval(value);

    if (
      Number.isInteger(value) &&
      value >= MIN_RC_CHIP_INTERVAL &&
      value <= MAX_RC_CHIP_INTERVAL
    ) {
      setErrorRCChipInterval(null);
    } else {
      setErrorRCChipInterval(
        t('ERROR_PROJECT_RC_CHIP_INTERVAL_RANGE', {
          min: MIN_RC_CHIP_INTERVAL,
          max: MAX_RC_CHIP_INTERVAL,
        }),
      );
    }
  };

  const reset = (): void => {
    if (!!projectId) {
      setProjectId('');
    }
    if (!!errorProjectId) {
      setErrorProjectId(null);
    }
    if (!!projectTitle) {
      setProjectTitle('');
    }
    if (!!projectDescription) {
      setProjectDescription('');
    }
    if (!!formError) {
      setFormError(undefined);
    }
    if (!!projectRCChipInterval) {
      setProjectRCChipInterval(DEFAULT_RC_CHIP_INTERVAL);
    }
  };

  return (
    <Dialog
      open={props.isOpen}
      onClose={() => {
        handleClose();
      }}
    >
      <DialogTitle>{t('DIALOG_TITLE_PROJECT_ADD_PROJECT')}</DialogTitle>
      <DialogContent>
        <Stack
          spacing={2}
          direction="column"
          alignContent="flex-start"
          justifyContent="flex-start"
        >
          {(
            t('DIALOG_CONTENT_PROJECT_ADD_PROJECT_INSTRUCTIONS', {
              returnObjects: true,
            }) as string[]
          ).map((paragraph: string, index: number) => {
            return <p key={`create-project-paragraph-${index}`}>{paragraph}</p>;
          })}
          <FormControl>
            <DebouncedTextField
              type="text"
              name="addProjectTitle"
              label={t('LABEL_PROJECT_ADD_PROJECT_TITLE')}
              placeholder={t('PLACEHOLDER_PROJECT_ADD_PROJECT_TITLE')}
              onChange={handleChange}
            />
          </FormControl>
          <FormControl>
            <TextField
              error={!!errorProjectId}
              type="text"
              name="addProjectId"
              value={projectId}
              label={t('LABEL_PROJECT_ADD_PROJECT_PROJECTID')}
              placeholder={t('PLACEHOLDER_PROJECT_ADD_PROJECT_PROJECTID')}
              onChange={(event) => {
                const { value, name } = event.target;
                setErrorProjectId(null);
                setAutoProjectId(false);
                setProjectId(value);
              }}
            />
            <FormHelperText className={errorProjectId ? 'Mui-error' : ''}>
              {errorProjectId || t('FORM_HELPER_PROJECTID_REGEX')}
            </FormHelperText>
          </FormControl>
          <FormControl>
            <DebouncedTextField
              type="text"
              multiline
              minRows={3}
              maxRows={3}
              name="addProjectDescription"
              label={t('LABEL_PROJECT_ADD_PROJECT_DESCRIPTION')}
              placeholder={t('PLACEHOLDER_PROJECT_ADD_PROJECT_DESCRIPTION')}
              onChange={handleChange}
            />
          </FormControl>
          <FormControl>
            <InputLabel>{t('LABEL_PROJECT_ADD_PROJECT_UNIT')}</InputLabel>
            <Select
              value={projectUnit}
              onChange={handleUnitChange}
              label={t('LABEL_PROJECT_ADD_PROJECT_UNIT')}
            >
              {Object.entries(Unit).map(([key, value]) => (
                <MenuItem key={key} value={value}>
                  {t(`LABEL_PROJECT_ADD_PROJECT_UNIT_${key.toUpperCase()}`)}
                </MenuItem>
              ))}
            </Select>
            <Alert severity="warning" style={{ marginTop: '10px' }}>
              {t('LABEL_PROJECT_ADD_UNIT_TYPE_WARNING')}
            </Alert>
          </FormControl>
          <FormControl>
            <TextField
              id="rc-chip-interval"
              name="rc-chip-interval"
              type="number"
              label={t('LABEL_PROJECT_ADD_PROJECT_RC_CHIP_INTERVAL')}
              value={projectRCChipInterval.toString()}
              onChange={(event) => {
                handleRCChipIntervalChange(Number(event.target.value));
              }}
              InputProps={{
                inputProps: { min: DEFAULT_RC_CHIP_INTERVAL },
              }}
            />
            <FormHelperText className={errorRCChipInterval ? 'Mui-error' : ''}>
              {errorRCChipInterval}
            </FormHelperText>
          </FormControl>
          {formError && (
            <Alert severity="error">
              <AlertTitle>{t('ERROR_CREATE_PROJECT')}</AlertTitle>
              <pre>{formError}</pre>
            </Alert>
          )}
          <Alert severity="info" style={{ marginTop: '10px' }}>
            <Trans
              i18nKey={'ALERT_INFO_RC_CHIP_INTERVAL'}
              components={{
                strong: <strong />,
              }}
            />
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          onClick={() => {
            handleClose();
          }}
        >
          {t('BUTTON_CANCEL')}
        </Button>
        <Button
          variant="contained"
          disabled={!!errorProjectId || !projectTitle || !!errorRCChipInterval}
          onClick={() => {
            handleSubmit();
          }}
        >
          {t('BUTTON_CONTINUE')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateProjectPartial;
